import React, { useState } from 'react';

import ALink from '~/components/features/custom-link';
import { useQuantityInput } from '~/components/features/use-quantity-input';
import { CatalogueItem } from '~/utils/types';
import { useCart, useProgram } from '~/hooks';
import type { VariantSelection } from '~/hooks/useVariantSelection';
import { trackAddToCart } from '~/sdk';
import { isStoreReadOnly } from '~/utils/programAccess';
import VariantSelector, { dbVariantGroupLabel } from './variant-selector';
import styles from './product-purchase.module.scss';

const MAX_QTY = 300;

const fmtPoints = (n: number) => Math.round(n ?? 0).toLocaleString('es');

// Panel de compra (from-scratch). Jerarquia clara + selector de variantes y un
// unico boton "Agregar al carrito". Respeta modo solo-lectura por programa: el
// selector y los puntos se ven, pero no se puede agregar.
// `compact` (usado en la vista rapida) omite la descripcion larga.
// Quien lo monta le pasa `key` por producto: la variante y la cantidad no se
// arrastran de un producto a otro.
export default function ProductPurchase({
  product,
  selection,
  compact = false,
}: {
  product: CatalogueItem;
  selection: VariantSelection;
  compact?: boolean;
}) {
  const { addToCart, items, cartFeedback } = useCart();
  const { program } = useProgram();

  const [quantity, setQuantity] = useState(1);
  const qtyInput = useQuantityInput({
    value: quantity,
    onCommit: setQuantity,
    max: MAX_QTY,
  });

  const { award } = product;
  const readOnly = isStoreReadOnly(program?.id);

  // El cargador de pagina de un deploy anterior (cacheado por el navegador)
  // puede quedar sirviendo esta version con una pagina que no pasa `selection`.
  if (!selection) return null;

  const { lineItem: line, pending, needsDbVariant, unavailable, points, display } = selection;

  // DbVariant efectiva a agregar (undefined si el premio no tiene).
  const target = selection.dbVariants.length > 0 ? selection.dbVariant : undefined;
  const lineKey = line ? `${line.id}-${target?.id ?? 'no-variant'}` : null;
  const inCart =
    line != null &&
    items.some((it) => it.id === line.id && (it.variant?.id ?? null) === (target?.id ?? null));
  const status = lineKey && cartFeedback.itemKey === lineKey ? cartFeedback.status : 'idle';
  const disabled =
    !line || Boolean(pending) || needsDbVariant || unavailable || inCart || status === 'adding';

  const onAdd = () => {
    if (disabled || !line) return;
    addToCart(line, quantity, target);
    trackAddToCart({
      productId: String(line.award.id),
      productPoints: line.points,
      quantity,
      templateId: line.templateId != null ? String(line.templateId) : undefined,
    });
  };

  const ctaLabel = pending
    ? `Selecciona ${pending.name.toLowerCase()}`
    : unavailable
      ? 'No disponible'
      : needsDbVariant
        ? `Selecciona ${dbVariantGroupLabel(selection.dbVariants).toLowerCase()}`
        : inCart
          ? 'Ya en el carrito'
          : status === 'adding'
            ? 'Añadiendo...'
            : status === 'added'
              ? 'Añadido al carrito'
              : 'Agregar al carrito';

  const fromPrice = points.min !== points.max;

  return (
    <div className={styles.panel}>
      {award.brand?.name && <span className={styles.brand}>{award.brand.name}</span>}

      <h1 className={styles.name}>{display.name}</h1>

      {/* Meta: codigo + modelo (los de la variante elegida) */}
      {(display.code || display.model) && (
        <div className={styles.meta}>
          {display.code && (
            <span className={styles.metaChip}>
              <i className="fas fa-hashtag"></i>
              {display.code}
            </span>
          )}
          {display.model && (
            <span className={styles.metaChip}>
              <i className="fas fa-cube"></i>
              {display.model}
            </span>
          )}
        </div>
      )}

      {award.subcategories && award.subcategories.length > 0 && (
        <div className={styles.cats}>
          {award.subcategories.map((s, i) => (
            <React.Fragment key={s.id ?? s.name + i}>
              <ALink
                href={{ pathname: '/shop', query: { category: s.category?.id } }}
                className={styles.catLink}
              >
                {s.name}
              </ALink>
              {i < award.subcategories.length - 1 && <span className={styles.catSep}>·</span>}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* Puntos: los de la variante elegida, o «Desde X» si aún varían */}
      <div className={styles.points} aria-live="polite">
        {fromPrice && <span className={styles.pointsFrom}>Desde</span>}
        <span className={styles.pointsValue}>{fmtPoints(points.min)}</span>
        <span className={styles.pointsCoin}>{program?.coinName ?? ''}</span>
      </div>

      {/* gptDescription oculta por ahora (a pedido). */}

      <div className={styles.buy}>
        <VariantSelector selection={selection} />

        {/* Compra (oculta en modo solo-lectura) */}
        {!readOnly && (
          <div className={styles.actions}>
            <div className={styles.qty}>
              <button
                type="button"
                className={styles.qtyBtn}
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                aria-label="Disminuir"
              >
                <i className="fas fa-minus"></i>
              </button>
              <input
                className={styles.qtyInput}
                type="text"
                inputMode="numeric"
                aria-label="Cantidad"
                value={qtyInput.text}
                onChange={(e) => qtyInput.onChange(e.target.value)}
                onBlur={qtyInput.onBlur}
                onFocus={(e) => e.target.select()}
              />
              <button
                type="button"
                className={styles.qtyBtn}
                onClick={() => setQuantity((q) => Math.min(MAX_QTY, q + 1))}
                aria-label="Aumentar"
              >
                <i className="fas fa-plus"></i>
              </button>
            </div>

            <button
              type="button"
              className={`${styles.cta} ${status === 'added' || inCart ? styles.ctaDone : ''}`}
              disabled={disabled}
              onClick={onAdd}
            >
              <i className={status === 'added' || inCart ? 'fas fa-check' : 'fas fa-cart-plus'}></i>
              <span>{ctaLabel}</span>
            </button>
          </div>
        )}
      </div>

      {/* Descripcion larga */}
      {!compact && award.description && (
        <div className={styles.descBlock}>
          <span className={styles.descTitle}>Descripción</span>
          <div
            className={styles.descBody}
            dangerouslySetInnerHTML={{ __html: award.description }}
          />
        </div>
      )}
    </div>
  );
}
